import DocumentParticipantField from '@app/store/contract-setup/ParticipantField';
import { RootInPage } from '@app/store/document/renderTree';
import DraftParticipant from '@app/store/embedded/Participant';
import FieldNode from '@app/store/FieldNode';
import FieldType from '@app/store/FieldType';
import Participant from '@app/store/template/Participant';

// Exposed first
export function buildPageRootFromTemplateParticipants(participants: Participant[]) {
  return buildPageRootFromParticipantArrayInternal(participants);
}

export function buildPageRootFromDraftParticipants(fields: DraftParticipant[]) {
  const internal: InternalField[] = [];
  fields.forEach((participant) => {
    participant.fields.forEach((field) => {
      if (field.type === FieldType.CHECKBOX_GROUP) {
        internal.push({
          fieldId: field.id,
          fieldType: FieldType.CHECKBOX_GROUP,
          guide: {
            locationPage: field.position.page,
          },
          children: field.children.map((child) => {
            return {
              fieldId: child.id,
              fieldType: FieldType.CHECKBOX_FIELD,
            };
          }),
        });
      } else {
        internal.push({
          fieldId: field.id,
          fieldType: field.type,
          guide: {
            locationPage: field.position.page,
          },
        });
      }
    });
  });
  return buildPageRootInternal(internal);
}

export function buildPageRootFromDocumentParticipantFields(fields: DocumentParticipantField[]) {
  return buildPageRootInternal(fields);
}

function insertNode(
  roots: RootInPage,
  page: number,
  id: string,
  type: FieldType,
  children?: FieldNode[]
) {
  if (!roots[page]) {
    roots[page] = [];
  }
  roots[page].push({
    id,
    type,
    children,
  });
}

type InternalField = {
  fieldId: string;
  fieldType: FieldType;
  guide: {
    locationPage: number;
  };
  children?: { fieldId: string; fieldType: FieldType }[];
};

interface InternalParticipant {
  fields: {
    checkboxGroups: {
      id: string;
      page: number;
      children: { id: string }[];
    }[];
    signatureFields: {
      id: string;
      page: number;
    }[];
    textFields: {
      id: string;
      page: number;
    }[];
  };
}

/*
 InternalField를 렌더트리로 변환합니다.
 렌더트리를 생성하는 구체적인 알고리즘은 명세에 기술되어 있으며 아래는 그 구현입니다.
 see: https://www.notion.so/modusign/abca6a34266947e8b52e4b57c6cad434#756c82029dd545cdab2262b5b6ef7535
 FIXME: short url이 있으면 좋을것 같아요
*/
// Do not export
function buildPageRootInternal(fields: InternalField[]) {
  const roots: RootInPage = {};
  fields.forEach((field) => {
    const childIfExisted = field.children?.map(({ fieldId, fieldType }) => {
      return {
        id: fieldId,
        type: fieldType,
      };
    });
    insertNode(roots, field.guide.locationPage, field.fieldId, field.fieldType, childIfExisted);
  });
  return roots;
}

// Do not export
function buildPageRootFromParticipantArrayInternal(participants: InternalParticipant[]) {
  return participants.reduce<RootInPage>((acc, cur) => {
    const internal: InternalField[] = [
      ...cur.fields.checkboxGroups.map((checkboxGroup) => {
        return {
          fieldId: checkboxGroup.id,
          fieldType: FieldType.CHECKBOX_GROUP,
          guide: { locationPage: checkboxGroup.page },
          children: checkboxGroup.children?.map((child) => {
            return {
              fieldId: child.id,
              fieldType: FieldType.CHECKBOX_FIELD,
            };
          }),
        };
      }),
      ...cur.fields.signatureFields.map((signatureField) => {
        return {
          fieldId: signatureField.id,
          fieldType: FieldType.SIGNATURE_FIELD,
          guide: { locationPage: signatureField.page },
        };
      }),
      ...cur.fields.textFields.map((textField) => {
        return {
          fieldId: textField.id,
          fieldType: FieldType.TEXT_FIELD,
          guide: { locationPage: textField.page },
        };
      }),
    ];

    const root = buildPageRootInternal(internal);
    Object.keys(root).forEach((page) => {
      if (!acc[+page]) {
        acc[+page] = [];
      }
      root[+page].forEach((node) => {
        acc[+page].push(node);
      });
    });
    return acc;
  }, {});
}
